import { useEffect, useRef, useState } from "react";
import { useDispatch } from "react-redux";

import useCreatePaymentMethod from "@hooks/billing/useCreatePaymentMethod";
import { ADD_PAYMENT_METHOD } from "@store/user/userActions";
import { PaymentRequest, PaymentRequestPaymentMethodEvent, Stripe } from "@stripe/stripe-js";
import { PaymentMethodManagerView } from "@types-runway/billing";

const APPLE_PAY_LABEL = "Runway Health";

interface UseApplePayOptions {
  isPaymentInFlight: () => boolean;
  setPaymentInFlight: (value: boolean) => void;
  onPaymentMethodAttached: () => void;
}

// Owns the Apple Pay-specific Stripe mechanics (creating the paymentRequest,
// listening for "paymentmethod", completing the native sheet, tokenizing) so
// CheckoutPage doesn't need to know any of that — mirroring how
// usePaymentCompletion owns WithFlex's postMessage mechanics. The in-flight
// guard is the one thing that has to stay external: it's shared with the
// card-form submit flow, which this hook has no reason to know about.
const useApplePay = (
  stripe: Stripe | null,
  amount: number,
  { isPaymentInFlight, setPaymentInFlight, onPaymentMethodAttached }: UseApplePayOptions
) => {
  const dispatch = useDispatch();
  const { mapStripePaymentMethodToDTO } = useCreatePaymentMethod();

  const [paymentRequest, setPaymentRequest] = useState<PaymentRequest | null>(null);
  const [canUseApplePay, setCanUseApplePay] = useState(false);

  const onPaymentMethodAttachedRef = useRef(onPaymentMethodAttached);
  onPaymentMethodAttachedRef.current = onPaymentMethodAttached;

  useEffect(() => {
    if (!stripe || amount <= 0) {
      setPaymentRequest(null);
      setCanUseApplePay(false);
      return;
    }

    const request = stripe.paymentRequest({
      country: "US",
      currency: "usd",
      total: { label: APPLE_PAY_LABEL, amount },
      requestPayerName: true,
      requestPayerEmail: true,
    });

    request.canMakePayment().then((result) => {
      setCanUseApplePay(!!result?.applePay);
    });

    setPaymentRequest(request);
  }, [stripe, amount]);

  useEffect(() => {
    if (!paymentRequest) {
      return;
    }

    const handlePaymentMethod = (event: PaymentRequestPaymentMethodEvent) => {
      if (isPaymentInFlight()) {
        event.complete("fail");
        return;
      }
      setPaymentInFlight(true);
      event.complete("success");

      const newPaymentMethod = mapStripePaymentMethodToDTO(event.paymentMethod);
      dispatch({
        type: ADD_PAYMENT_METHOD,
        paymentMethodId: newPaymentMethod.id,
        view: PaymentMethodManagerView.CHECKOUT,
      });
      onPaymentMethodAttachedRef.current();
    };

    paymentRequest.on("paymentmethod", handlePaymentMethod);
  }, [paymentRequest]);

  return { paymentRequest, canUseApplePay };
};

export default useApplePay;
